import type { Clip, Segment } from "@hudhud/core";
import { reelDurationFrames, FPS } from "@hudhud/reel";
import { timelineDuration, sourceToTimeline, openerHoldMs, timelineMusicMutes, timelineOverlays, momentsOnTimeline, timelineBehindTexts } from "@hudhud/core";

/** A piece of the speech as it sits on the timeline. */
export interface Block { i: number; tStart: number; dur: number; seg: Segment }

/** What is selected on the strip: nothing has handles until it is. */
export type Sel =
  | { kind: "piece"; i: number }
  | { kind: "cut"; i: number }
  | { kind: "opener" }
  | { kind: "end" }
  | { kind: "picture"; id: string }
  | { kind: "behind"; id: string }
  | { kind: "quiet"; i: number }
  | { kind: "beat"; id: string }
  | { kind: "mark"; ms: number }
  | null;

export const sameSel = (a: Sel, b: Sel): boolean => JSON.stringify(a) === JSON.stringify(b);

export function blocksOf(segments: Segment[], hold: number): Block[] {
  let t = hold;
  return segments.map((seg, i) => { const b = { i, tStart: t, dur: seg.outMs - seg.inMs, seg }; t += b.dur; return b; });
}

/** The slot (0..n) a piece dropped at a timeline time lands in: past a block's middle means after it. */
export function slotAt(blocks: Block[], ms: number): number {
  let idx = 0;
  for (const b of blocks) if (ms > b.tStart + b.dur / 2) idx = b.i + 1;
  return Math.max(0, Math.min(blocks.length, idx));
}

/** The ruler's tick spacing: labels stay at least minPx apart at any zoom. */
export function tickStep(px: number, minPx = 56): number {
  return [500, 1000, 2000, 5000, 10000, 15000, 30000].find((st) => st * px >= minPx) ?? 60000;
}
export const fmtTime = (ms: number) => `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, "0")}`;
export const fmtTick = (ms: number, step: number) => (step < 1000 ? (ms / 1000).toFixed(1) : fmtTime(ms));
export const fmtSec = (ms: number) => `${(ms / 1000).toFixed(1)}s`;

/** How many times one piece plays (a duplicated piece shows a ×2). */
export const copiesOf = (blocks: Block[], b: Block) => blocks.filter((x) => x.seg.inMs === b.seg.inMs && x.seg.outMs === b.seg.outMs).length;

/** Pixels per millisecond: zoom 1 puts the whole clip across the view. */
export const pxPerMs = (viewW: number, zoom: number, totalMs: number) => (viewW * zoom) / Math.max(1, totalMs);

/** Everything the strip draws, computed once per clip / zoom / width. */
export function buildModel(clip: Clip, viewW: number, zoom: number) {
  const speechMs = timelineDuration(clip.segments);
  const hold = openerHoldMs(clip); // a full-screen opener before the speech shifts the whole strip right
  const totalMs = (reelDurationFrames(clip) / FPS) * 1000;
  const px = pxPerMs(viewW, zoom, totalMs);
  const tm = timelineMusicMutes(clip);
  const quiet = tm.whole ? [{ inMs: -hold, outMs: speechMs + clip.endCard.durationMs + 1000, own: -1 }] : tm.ranges.map((r) => ({ ...r, own: clip.edits!.musicMutes.findIndex((m) => (sourceToTimeline(clip.segments, m.inMs) ?? -1) === r.inMs) }));
  const pictures = timelineOverlays({ segments: clip.segments, overlays: clip.overlays.map((o) => ({ ...o, image: o.image ?? "pending" })) });
  const behind = timelineBehindTexts({ segments: clip.segments, behindTexts: clip.behindTexts.map((t) => ({ ...t, text: t.text.trim() || "…" })) });
  const beats = momentsOnTimeline(clip);
  const blocks = blocksOf(clip.segments, hold);
  const marks = clip.edits!.zoomMarks.map((ms) => ({ ms, t: sourceToTimeline(clip.segments, ms) })).filter((m) => m.t != null).map((m) => ({ ms: m.ms, t: (m.t as number) + hold }));
  return { speechMs, hold, totalMs, px, quiet, pictures, behind, beats, blocks, marks };
}
export type Model = ReturnType<typeof buildModel>;

/** Where cut i (after piece i) sits on the timeline, in ms. */
export const cutMs = (blocks: Block[], i: number): number => blocks[i].tStart + blocks[i].dur;
